// Global Instructions Rule Applied!
//
// Relationships — HTTP service (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 0). Talks to the Python backend only (CLAUDE.md — never Supabase
// directly); every URL comes from API_ROUTES; every response is validated with
// its Zod model before it reaches UI state.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AccountOwnerRecord,
  AccountFundExposure,
  LpAllocationRow,
  LpAllocationFilterOptions,
  RelationshipFactRecord,
  AccountOwnerRoleAdminRow,
  RelationshipFactTypeAdminRow,
  validateAccountOwnersList,
  validateAccountFundExposure,
  validateLpAllocationRows,
  validateLpAllocationFilterOptions,
  validateRelationshipFactsList,
  validateAccountOwnerRoleAdminRows,
  validateRelationshipFactTypeAdminRows
} from '../../../state_manager/data_models/relationships_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// The backend's own sentence, never a bare status line (design patterns:
// "Feedback on a blocked action").
const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.message || body?.error || `${fallback}: ${response.status} ${response.statusText}`
}

const request = async (url: string, init: RequestInit, accessToken: string | null, fallback: string): Promise<any> => {
  const response = await fetch(url, { ...init, headers: jsonHeaders(accessToken) })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(await readError(response, fallback))
  }
  return response.json()
}

export interface ListResult<T> {
  data: T[]
  has_more: boolean
  count: number
}

// ── Account owners ─────────────────────────────────────────────────────────
export const fetchAccountOwners = async (accountId: string, accessToken: string | null): Promise<AccountOwnerRecord[]> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.ACCOUNT_OWNERS, { accountId })
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load owners')
  return validateAccountOwnersList(json.data ?? [])
}

// Phase 8: the owner is a person — contact_id OR system_user_id (the RPC
// resolves the contact); owner_role_id is an account_ownership title id.
export interface AccountOwnerSavePayload {
  id?: string
  account_id?: string
  contact_id?: string
  system_user_id?: string
  owner_role_id?: string
  from_date?: string
  to_date?: string | null
  notes?: string | null
}

export const saveAccountOwner = async (payload: AccountOwnerSavePayload, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.ACCOUNT_OWNER_SAVE)
  await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to save owner')
}

export const endAccountOwner = async (ownerId: string, effectiveTo: string | null, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.ACCOUNT_OWNER_END, { ownerId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: { to_date: effectiveTo } }) }, accessToken, 'Failed to end assignment')
}

export const deleteAccountOwner = async (ownerId: string, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.ACCOUNT_OWNER_DELETE, { ownerId })
  await request(url, { method: 'DELETE' }, accessToken, 'Failed to delete assignment')
}

// ── Per-LP agent drafts toggle (PR D) ──────────────────────────────────────
/** accounts.agent_drafts_enabled — tri-state. null = the LP status cadence
 *  policy decides; true = drafts even where the status says signal only;
 *  false = no agent draft of any kind for this LP. Rides the accounts PATCH
 *  route the InputManager autosave uses ({id, <column>: value}). */
export const setAccountAgentDrafts = async (
  accountId: string,
  value: boolean | null,
  accessToken: string | null
): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.PATCH)
  await request(
    url,
    { method: 'PATCH', body: JSON.stringify({ id: accountId, agent_drafts_enabled: value }) },
    accessToken,
    'Failed to update agent drafts for this LP'
  )
}

// ── LP allocation grid ─────────────────────────────────────────────────────
export interface LpAllocationQuery {
  filters?: Array<{ key: string; value: unknown }>
  sorting?: Array<{ key: string; direction: 'asc' | 'desc' }>
  page?: number
  page_size?: number
}

export const fetchLpAllocation = async (query: LpAllocationQuery, accessToken: string | null): Promise<ListResult<LpAllocationRow>> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.LP_ALLOCATION)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: query }) }, accessToken, 'Failed to load LP allocation')
  return { data: validateLpAllocationRows(json.data ?? []), has_more: Boolean(json.has_more), count: Number(json.count ?? 0) }
}

/** Bulk actions (PR E). Targets: `accountIds` (the rows selected on the grid)
 *  or, when null, every LP matching `filters` — the grid's current filter
 *  set, which the server resolves through the same row set the grid reads.
 *  Admin-gated server-side. */
export interface LpAllocationBulkTargets {
  accountIds: string[] | null
  filters: Array<{ key: string; value: unknown }>
}
export interface LpAllocationBulkResult {
  targeted: number
  updated: number
  errors?: Array<{ account_id: string; error: string }>
}
const bulkBody = (t: LpAllocationBulkTargets, extra: Record<string, unknown>) =>
  JSON.stringify({ data: { ...(t.accountIds ? { account_ids: t.accountIds } : { filters: t.filters }), ...extra } })

export const bulkSetLpAgentDrafts = async (
  targets: LpAllocationBulkTargets,
  value: boolean | null,
  accessToken: string | null
): Promise<LpAllocationBulkResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.LP_ALLOCATION_BULK_AGENT_DRAFTS)
  const json = await request(url, { method: 'POST', body: bulkBody(targets, { value }) }, accessToken, 'Bulk update failed')
  return (json.data ?? {}) as LpAllocationBulkResult
}

export const bulkSetLpOwner = async (
  targets: LpAllocationBulkTargets,
  ownerRoleId: string,
  systemUserId: string,
  accessToken: string | null
): Promise<LpAllocationBulkResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.LP_ALLOCATION_BULK_OWNER)
  const json = await request(
    url,
    { method: 'POST', body: bulkBody(targets, { owner_role_id: ownerRoleId, system_user_id: systemUserId }) },
    accessToken,
    'Bulk update failed'
  )
  return (json.data ?? {}) as LpAllocationBulkResult
}

export const fetchLpAllocationFilterOptions = async (accessToken: string | null): Promise<LpAllocationFilterOptions> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.LP_ALLOCATION_FILTER_OPTIONS)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to load filter options')
  return validateLpAllocationFilterOptions(json.data ?? {})
}

// ── Relationship facts ─────────────────────────────────────────────────────
export const fetchRelationshipFacts = async (
  accountId: string,
  params: { prospectId?: string | null; status?: string[]; includeHistory?: boolean },
  accessToken: string | null
): Promise<RelationshipFactRecord[]> => {
  const query: Record<string, string | boolean> = {}
  if (params.prospectId) query.prospect_id = params.prospectId
  if (params.status && params.status.length) query.status = params.status.join(',')
  if (params.includeHistory) query.include_history = true
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.RELATIONSHIP_FACTS, { accountId }, query)
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load relationship facts')
  return validateRelationshipFactsList(json.data ?? [])
}

export interface RelationshipFactSavePayload {
  id?: string
  account_id?: string
  fact_type_id?: string
  content?: string
  contact_id?: string | null
  prospect_id?: string | null
  importance?: number
  structured?: Record<string, unknown>
}

export const saveRelationshipFact = async (payload: RelationshipFactSavePayload, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.RELATIONSHIP_FACT_SAVE)
  await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to save fact')
}

export const reviewRelationshipFact = async (
  factId: string,
  decision: 'accept' | 'reject',
  reason: string | null,
  accessToken: string | null,
  contentOverride?: string | null
): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.RELATIONSHIP_FACT_REVIEW, { factId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: { decision, reason, content_override: contentOverride ?? null } }) }, accessToken, 'Failed to review fact')
}

export const archiveRelationshipFact = async (factId: string, reason: string | null, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.RELATIONSHIP_FACT_ARCHIVE, { factId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: { reason } }) }, accessToken, 'Failed to archive fact')
}

export const confirmRelationshipFact = async (factId: string, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.RELATIONSHIP_FACT_CONFIRM, { factId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to confirm fact')
}

// ── LP fund exposure ───────────────────────────────────────────────────────
export const fetchAccountFundExposure = async (accountId: string, accessToken: string | null, asOf?: string): Promise<AccountFundExposure> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.ACCOUNT_FUND_EXPOSURE, { accountId }, asOf ? { as_of: asOf } : {})
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load fund exposure')
  return validateAccountFundExposure(json.data ?? {})
}

// ── Configuration lookups (admin) ──────────────────────────────────────────
export const fetchAccountOwnerRoles = async (accessToken: string | null): Promise<AccountOwnerRoleAdminRow[]> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_ACCOUNT_OWNER_ROLES)
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load owner roles')
  return validateAccountOwnerRoleAdminRows(json.data ?? [])
}

export interface AccountOwnerRolePayload {
  title_key?: string
  name?: string
  description?: string | null
  is_exclusive?: boolean
  sort_order?: number
}

export const saveAccountOwnerRole = async (id: string | null, payload: AccountOwnerRolePayload, accessToken: string | null): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_ACCOUNT_OWNER_ROLES)
  await request(id ? `${base}/${id}` : base, { method: id ? 'PUT' : 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to save owner role')
}

export const deleteAccountOwnerRole = async (id: string, accessToken: string | null): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_ACCOUNT_OWNER_ROLES)
  await request(`${base}/${id}`, { method: 'DELETE' }, accessToken, 'Failed to delete owner role')
}

export const fetchRelationshipFactTypes = async (accessToken: string | null): Promise<RelationshipFactTypeAdminRow[]> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_RELATIONSHIP_FACT_TYPES)
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load fact types')
  return validateRelationshipFactTypeAdminRows(json.data ?? [])
}

export interface RelationshipFactTypePayload {
  type_key?: string
  label?: string
  description?: string | null
  guidance?: string | null
  is_personal?: boolean
  visible_in_drafts?: boolean
  sort_order?: number
  enabled?: boolean
}

export const saveRelationshipFactType = async (id: string | null, payload: RelationshipFactTypePayload, accessToken: string | null): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_RELATIONSHIP_FACT_TYPES)
  await request(id ? `${base}/${id}` : base, { method: id ? 'PUT' : 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to save fact type')
}

export const deleteRelationshipFactType = async (id: string, accessToken: string | null): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.RELATIONSHIPS.CONFIG_RELATIONSHIP_FACT_TYPES)
  await request(`${base}/${id}`, { method: 'DELETE' }, accessToken, 'Failed to delete fact type')
}
